import React, { useEffect, useState, useMemo } from "react";
import MessengerLayout   from "../../layout/MessengerLayout";
import KanbanBoard       from "../projects/KanbanBoard";
import TaskListView      from "../projects/TaskListView";
import TaskDrawer        from "../projects/TaskDrawer";
import TeamWorkloadPanel from "../projects/TeamWorkloadPanel";
import ProjectAnalytics  from "../projects/ProjectAnalytics";
import { Plus, Pencil, X, CheckCheck, AlertTriangle, Star, ChevronRight, CheckSquare } from "lucide-react";
import { toast } from "../ui/Toast";
import { useConfirmDelete } from "../../hooks/useConfirmDelete";

const API   = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });
const card  = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const TABS = [
  { key: "overview",   label: "Overview"    },
  { key: "projects",   label: "Projects"    },
  { key: "tasks",      label: "Tasks"       },
  { key: "workload",   label: "Workload"    },
  { key: "approvals",  label: "Approvals"   },
  { key: "analytics",  label: "Analytics"   },
];

const STATUS_STYLE = {
  "Planning":  "text-white/40 bg-white/5 border-white/10",
  "Active":    "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  "On Hold":   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  "Completed": "text-blue-400 bg-blue-500/10 border-blue-500/20",
  "Cancelled": "text-red-400/50 bg-red-500/5 border-red-500/10",
};

function perfColor(s) {
  if (!s) return "rgba(255,255,255,0.2)";
  if (s >= 4.5) return "#00ff90";
  if (s >= 3.5) return "#60a5fa";
  if (s >= 2.5) return "#fbbf24";
  return "#f87171";
}

const EMPTY_PROJECT = {
  name: "", description: "", status: "Planning",
  start_date: "", end_date: "", member_ids: [], perf_weight: 10,
};

export default function TeamProjectsPage() {
  const [tab,           setTab]           = useState("overview");
  const [projects,      setProjects]      = useState([]);
  const [tasks,         setTasks]         = useState([]);
  const [members,       setMembers]       = useState([]);
  const [approvals,     setApprovals]     = useState([]);
  const [kpis,          setKpis]          = useState(null);
  const [selectedTask,  setSelectedTask]  = useState(null);
  const [activeProject, setActiveProject] = useState("ALL");
  const [taskView,      setTaskView]      = useState("kanban");
  const [memberFilter,  setMemberFilter]  = useState("ALL");
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);

  // Project form
  const [showProjForm, setShowProjForm] = useState(false);
  const [editingProj,  setEditingProj]  = useState(null);
  const [projDraft,    setProjDraft]    = useState(EMPTY_PROJECT);
  const [savingProj,   setSavingProj]   = useState(false);

  // Approval comments
  const [approvalComments, setApprovalComments] = useState({});

  const { confirmDelete, ConfirmDialog } = useConfirmDelete();

  useEffect(() => {
    setLoading(true); setError(null);
    Promise.all([
      fetch(`${API}/api/manager/projects`,                   { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/manager/projects/tasks`,             { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/manager/projects/members`,           { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/manager/projects/tasks/approvals`,   { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/manager/projects/kpis`,              { headers: authH() }).then(r => r.json()),
    ])
      .then(([p, t, m, a, k]) => {
        setProjects(Array.isArray(p) ? p : []);
        setTasks(Array.isArray(t) ? t : []);
        setMembers(Array.isArray(m) ? m : []);
        setApprovals(Array.isArray(a) ? a : []);
        setKpis(k || null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  /* ── Filtered tasks ── */
  const filteredTasks = useMemo(() => {
    let out = activeProject === "ALL" ? tasks : tasks.filter(t => t.project_id === activeProject);
    if (memberFilter !== "ALL") out = out.filter(t => t.assignee_id === memberFilter);
    return out;
  }, [tasks, activeProject, memberFilter]);

  /* ── Task actions ── */
  // Returns true on success, false on failure (after rolling back state and
  // showing the error toast). Callers can use the return value to decide
  // whether to show their own success toast.
  const patchTask = async (id, patch) => {
    let prevSnapshot;
    setTasks(prev => {
      prevSnapshot = prev;
      return prev.map(t => t.id === id ? { ...t, ...patch } : t);
    });
    try {
      const res = await fetch(`${API}/api/manager/projects/tasks/${id}`, {
        method: "PATCH", headers: authH(), body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return true;
    } catch (e) {
      if (prevSnapshot) setTasks(prevSnapshot);
      toast.error("Could not update task — changes reverted");
      return false;
    }
  };

  const createTask = async (draft) => {
    const res = await fetch(`${API}/api/manager/projects/tasks`, {
      method: "POST", headers: authH(), body: JSON.stringify(draft),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const created = await res.json();
    setTasks(prev => [...prev, created]);
    return created;
  };

  // Confirmation + toast pair are owned by TaskDrawer's own useConfirmDelete.
  // This handler performs the API call and throws on failure so the drawer's
  // catch block fires the error toast.
  const deleteTask = async (id) => {
    const res = await fetch(`${API}/api/manager/projects/tasks/${id}`, { method: "DELETE", headers: authH() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    setTasks(prev => prev.filter(t => t.id !== id));
    setSelectedTask(null);
  };

  /* ── Project actions ── */
  const saveProject = async () => {
    if (!projDraft.name) return;
    setSavingProj(true);
    try {
      const isNew = !editingProj;
      const res = await fetch(
        isNew ? `${API}/api/manager/projects` : `${API}/api/manager/projects/${editingProj}`,
        { method: isNew ? "POST" : "PATCH", headers: authH(), body: JSON.stringify(projDraft) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = await res.json();
      if (isNew) setProjects(prev => [...prev, saved]);
      else setProjects(prev => prev.map(p => p.id === editingProj ? { ...p, ...projDraft } : p));
      setShowProjForm(false); setProjDraft(EMPTY_PROJECT); setEditingProj(null);
      toast.success("Project saved");
    } catch (e) { setError(e.message); toast.error("Failed to save project"); }
    finally { setSavingProj(false); }
  };

  const deleteProject = (id) => {
    confirmDelete("this project", async () => {
      try {
        const res = await fetch(`${API}/api/manager/projects/${id}`, { method: "DELETE", headers: authH() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setProjects(prev => prev.filter(p => p.id !== id));
        if (activeProject === id) setActiveProject("ALL");
        toast.success("Project deleted");
      } catch (e) {
        toast.error("Failed to delete project");
      }
    });
  };

  const startEditProject = (p) => {
    setProjDraft({ ...p, member_ids: p.member_ids || [] });
    setEditingProj(p.id);
    setShowProjForm(true);
  };

  const toggleMember = (id) => setProjDraft(d => ({
    ...d,
    member_ids: d.member_ids.includes(id) ? d.member_ids.filter(x => x !== id) : [...d.member_ids, id],
  }));

  /* ── Approval actions ── */
  const handleApproval = async (taskId, approved) => {
    try {
      const res = await fetch(`${API}/api/manager/projects/tasks/${taskId}/approve-completion`, {
        method: "POST", headers: authH(),
        body: JSON.stringify({ approved, note: approvalComments[taskId] || "" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setApprovals(prev => prev.filter(a => a.id !== taskId));
      if (approved) patchTask(taskId, { completion_approved: true });
      toast.success(approved ? "Task approved" : "Task rejected");
    } catch (e) {
      setError(e.message);
      toast.error(approved ? "Failed to approve task" : "Failed to reject task");
    }
  };

  /* ── Derived KPIs ── */
  const derived = useMemo(() => {
    const total      = tasks.length;
    const done       = tasks.filter(t => t.status === "Done").length;
    const inProg     = tasks.filter(t => t.status === "In Progress").length;
    const overdue    = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== "Done").length;
    const unassigned = tasks.filter(t => !t.assignee_id).length;
    const completion = total ? Math.round((done / total) * 100) : 0;
    return { total, done, inProg, overdue, unassigned, completion };
  }, [tasks]);

  const stats = kpis || derived;

  const projectCompletion = (pid) => {
    const pt = tasks.filter(t => t.project_id === pid);
    return pt.length ? Math.round((pt.filter(t => t.status === "Done").length / pt.length) * 100) : 0;
  };

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#00ff90]">Team Projects</h1>
            <p className="text-white/40 text-sm mt-0.5">Manage your team's projects, tasks and performance</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {projects.length > 0 && (
              <select value={activeProject} onChange={e => setActiveProject(e.target.value)}
                className="bg-black border border-[#00ff90]/20 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors">
                <option value="ALL">All Projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            {members.length > 0 && (
              <select value={memberFilter} onChange={e => setMemberFilter(e.target.value)}
                className="bg-black border border-[#00ff90]/20 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors">
                <option value="ALL">All Members</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* KPI chips */}
        <div className={`${card} p-5`}>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              { label: "Total Tasks",   value: stats.total,             color: "#00ff90"  },
              { label: "In Progress",   value: stats.inProg ?? stats.in_progress, color: "#60a5fa" },
              { label: "Completed",     value: stats.done ?? stats.completed,     color: "#34d399" },
              { label: "Completion %",  value: `${stats.completion ?? 0}%`,       color: "#fbbf24" },
              { label: "Overdue",       value: stats.overdue,           color: stats.overdue > 0 ? "#f87171" : "rgba(255,255,255,0.2)" },
              { label: "Unassigned",    value: stats.unassigned,        color: stats.unassigned > 0 ? "#fb923c" : "rgba(255,255,255,0.2)" },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
                {loading ? <div className="h-6 rounded bg-white/10 animate-pulse mb-1" /> : (
                  <p className="text-lg font-bold" style={{ color }}>{value ?? "—"}</p>
                )}
                <p className="text-xs text-white/30 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
          {approvals.length > 0 && (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-yellow-500/20 bg-yellow-500/5 px-4 py-2">
              <AlertTriangle size={13} className="text-yellow-400 shrink-0" />
              <p className="text-xs text-yellow-400">{approvals.length} task completion{approvals.length !== 1 ? "s" : ""} awaiting your approval</p>
              <button onClick={() => setTab("approvals")} className="text-xs text-yellow-400 underline ml-auto">Review</button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/5 pb-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key ? "border-[#00ff90] text-[#00ff90]" : "border-transparent text-white/40 hover:text-white/70"
              }`}>
              {t.label}
              {t.key === "approvals" && approvals.length > 0 && (
                <span className="ml-1.5 text-xs bg-yellow-400 text-black rounded-full px-1.5 py-0.5 font-bold">{approvals.length}</span>
              )}
            </button>
          ))}
        </div>

        {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

        {/* ── OVERVIEW ── */}
        {tab === "overview" && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className="xl:col-span-2">
                <KanbanBoard
                  tasks={filteredTasks.filter(t => t.status !== "Done").slice(0, 15)}
                  members={members}
                  onMove={(id, status) => patchTask(id, { status })}
                  onOpen={setSelectedTask}
                  compact
                />
              </div>
              <TeamWorkloadPanel members={members} tasks={filteredTasks} compact />
            </div>
          </div>
        )}

        {/* ── PROJECTS ── */}
        {tab === "projects" && (
          <div className="space-y-5">

            {/* Project form */}
            {showProjForm && (
              <div className={`${card} p-5 space-y-4`}>
                <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
                  {editingProj ? "Edit Project" : "New Project"}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={labelCls}>Project Name</label>
                    <input className={inputCls} value={projDraft.name} onChange={e => setProjDraft(d => ({...d, name: e.target.value}))} placeholder="e.g. Q3 Feature Rollout" />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Description</label>
                    <textarea rows={2} className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 resize-none"
                      value={projDraft.description || ""} onChange={e => setProjDraft(d => ({...d, description: e.target.value}))} />
                  </div>
                  <div>
                    <label className={labelCls}>Status</label>
                    <select className={inputCls} value={projDraft.status} onChange={e => setProjDraft(d => ({...d, status: e.target.value}))}>
                      {Object.keys(STATUS_STYLE).map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Performance Weight %</label>
                    <input type="number" min="0" max="100" className={inputCls} value={projDraft.perf_weight ?? 10}
                      onChange={e => setProjDraft(d => ({...d, perf_weight: Number(e.target.value)}))} />
                  </div>
                  <div>
                    <label className={labelCls}>Start Date</label>
                    <input type="date" className={inputCls} value={projDraft.start_date || ""} onChange={e => setProjDraft(d => ({...d, start_date: e.target.value}))} />
                  </div>
                  <div>
                    <label className={labelCls}>End Date</label>
                    <input type="date" className={inputCls} value={projDraft.end_date || ""} onChange={e => setProjDraft(d => ({...d, end_date: e.target.value}))} />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Assign Team Members</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-36 overflow-y-auto mt-1">
                    {members.map(m => {
                      const active = (projDraft.member_ids || []).includes(m.id);
                      return (
                        <button key={m.id} onClick={() => toggleMember(m.id)}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-all text-left ${
                            active ? "border-[#00ff90]/40 bg-[#00ff90]/8 text-[#00ff90]" : "border-white/5 bg-white/3 text-white/50 hover:border-white/15"
                          }`}>
                          <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[10px] shrink-0">
                            {m.name?.[0]?.toUpperCase()}
                          </div>
                          <span className="truncate">{m.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
                  <button onClick={() => { setShowProjForm(false); setProjDraft(EMPTY_PROJECT); setEditingProj(null); }}
                    className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                  <button onClick={saveProject} disabled={savingProj || !projDraft.name}
                    className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
                    {savingProj ? "Saving…" : editingProj ? "Update" : "Create Project"}
                  </button>
                </div>
              </div>
            )}

            {/* Project list */}
            <div className={card}>
              <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Team Projects</span>
                <button onClick={() => { setProjDraft(EMPTY_PROJECT); setEditingProj(null); setShowProjForm(v => !v); }}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                  <Plus size={12} /> New Project
                </button>
              </div>
              <div className="divide-y divide-white/5">
                {projects.length === 0 ? (
                  <p className="px-5 py-8 text-white/20 text-sm italic text-center">No projects yet.</p>
                ) : projects.map(p => {
                  const pct      = projectCompletion(p.id);
                  const ptasks   = tasks.filter(t => t.project_id === p.id);
                  const pMembers = members.filter(m => (p.member_ids || []).includes(m.id));
                  return (
                    <div key={p.id} className="px-5 py-4 hover:bg-white/3 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="text-sm text-white/80 font-semibold">{p.name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[p.status] || STATUS_STYLE.Planning}`}>{p.status}</span>
                            {p.perf_weight > 0 && (
                              <span className="text-xs text-[#00ff90]/40 border border-[#00ff90]/15 px-2 py-0.5 rounded-full">{p.perf_weight}% perf</span>
                            )}
                          </div>
                          {p.description && <p className="text-xs text-white/30 mb-2">{p.description}</p>}
                          <div className="flex items-center gap-4 flex-wrap text-xs text-white/30">
                            {p.end_date && <span>Due: {p.end_date}</span>}
                            <span><CheckSquare size={11} className="inline mr-1" />{ptasks.filter(t => t.status === "Done").length}/{ptasks.length}</span>
                          </div>
                          {pMembers.length > 0 && (
                            <div className="flex items-center gap-1 mt-2">
                              {pMembers.slice(0, 6).map(m => (
                                <div key={m.id} title={m.name}
                                  className="w-6 h-6 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[10px] text-white/50">
                                  {m.name?.[0]?.toUpperCase()}
                                </div>
                              ))}
                              {pMembers.length > 6 && <span className="text-xs text-white/25">+{pMembers.length - 6}</span>}
                            </div>
                          )}
                          {ptasks.length > 0 && (
                            <div className="flex items-center gap-2 mt-3">
                              <div className="flex-1 h-1.5 rounded-full bg-white/5">
                                <div className="h-full rounded-full bg-[#00ff90]" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs text-[#00ff90]/50 w-8 text-right">{pct}%</span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => { setActiveProject(p.id); setTab("tasks"); }}
                            className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors flex items-center gap-1">
                            Tasks <ChevronRight size={12} />
                          </button>
                          <button onClick={() => startEditProject(p)}
                            className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => deleteProject(p.id)}
                            className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── TASKS ── */}
        {tab === "tasks" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => setTaskView("kanban")}
                className={`text-xs px-4 py-2 rounded-lg border transition-colors ${taskView === "kanban" ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/40 hover:text-white"}`}>
                Kanban
              </button>
              <button onClick={() => setTaskView("list")}
                className={`text-xs px-4 py-2 rounded-lg border transition-colors ${taskView === "list" ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/40 hover:text-white"}`}>
                List
              </button>
              <button onClick={() => setSelectedTask({ _new: true, project_id: activeProject !== "ALL" ? activeProject : "" })}
                className="ml-auto text-xs px-4 py-2 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 transition-colors">
                + New Task
              </button>
            </div>

            {taskView === "kanban" ? (
              <KanbanBoard
                tasks={filteredTasks}
                members={members}
                projects={projects}
                onMove={(id, status) => patchTask(id, { status })}
                onOpen={setSelectedTask}
                onCreate={draft => setSelectedTask({ _new: true, ...draft })}
              />
            ) : (
              <TaskListView
                tasks={filteredTasks}
                members={members}
                projects={projects}
                onOpen={setSelectedTask}
                onMove={(id, status) => patchTask(id, { status })}
                onAssign={(id, assignee_id) => patchTask(id, { assignee_id })}
              />
            )}
          </div>
        )}

        {/* ── WORKLOAD ── */}
        {tab === "workload" && (
          <TeamWorkloadPanel members={members} tasks={filteredTasks} projects={projects} />
        )}

        {/* ── APPROVALS ── */}
        {tab === "approvals" && (
          <div className={card}>
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Task Completion Approvals</span>
              <span className="text-xs text-white/30">{approvals.length} pending</span>
            </div>
            <div className="divide-y divide-white/5">
              {approvals.length === 0 ? (
                <p className="px-5 py-8 text-white/20 text-sm italic text-center">No completions pending approval.</p>
              ) : approvals.map(a => {
                const assignee = members.find(m => m.id === a.assignee_id);
                return (
                  <div key={a.id} className="px-5 py-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-white/80 font-medium">{a.title}</p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {assignee && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/50">
                                {assignee.name?.[0]?.toUpperCase()}
                              </div>
                              <span className="text-xs text-white/50">{assignee.name}</span>
                            </div>
                          )}
                          {a.project_name && <span className="text-xs text-white/30">{a.project_name}</span>}
                          {a.perf_score_weight > 0 && (
                            <span className="flex items-center gap-1 text-xs text-[#00ff90]/50">
                              <Star size={10} /> +{a.perf_score_weight} perf pts
                            </span>
                          )}
                          {a.goal_title && <span className="text-xs text-[#00ff90]/40">⚡ {a.goal_title}</span>}
                        </div>
                        {a.completion_note && (
                          <p className="text-xs text-white/30 mt-1 italic">"{a.completion_note}"</p>
                        )}
                      </div>
                      <button onClick={() => setSelectedTask(a)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors shrink-0">
                        View
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input placeholder="Note (optional)…" value={approvalComments[a.id] || ""}
                        onChange={e => setApprovalComments(c => ({ ...c, [a.id]: e.target.value }))}
                        className="flex-1 bg-black border border-[#00ff90]/20 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-[#00ff90]/40" />
                      <button onClick={() => handleApproval(a.id, true)}
                        className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                        <CheckCheck size={12} /> Approve
                      </button>
                      <button onClick={() => handleApproval(a.id, false)}
                        className="text-xs px-3 py-2 rounded-lg border border-red-500/20 bg-red-500/5 text-red-400/60 hover:bg-red-500/15 hover:text-red-400 transition-colors">
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── ANALYTICS ── */}
        {tab === "analytics" && (
          <ProjectAnalytics tasks={filteredTasks} members={members} projects={projects} />
        )}

      </div>

      <TaskDrawer
        open={!!selectedTask}
        task={selectedTask}
        members={members}
        projects={projects}
        onClose={() => setSelectedTask(null)}
        onSave={async (draft) => {
          try {
            if (draft._new || !draft.id) {
              await createTask(draft);
              toast.success("Task saved");
            } else {
              const ok = await patchTask(draft.id, draft);
              // patchTask owns its own error toast + rollback on failure
              if (ok) toast.success("Task saved");
            }
            setSelectedTask(null);
          } catch (e) {
            toast.error("Failed to save task");
          }
        }}
        onDelete={deleteTask}
        onMove={(id, status) => patchTask(id, { status })}
      />
      {ConfirmDialog}
    </MessengerLayout>
  );
}